import type { PartyLink, PartySourceBlock } from '../../types';
import { NOT_AVAILABLE } from '../utils';
import { ExpandAccordion, ExpandTable, PartyCellList } from './common';

interface Row {
  label: string;
  // One party list per source column, positionally aligned to `blocks`.
  cells: PartyLink[][];
}

/**
 * The expanded typed card's Parties section: role rows (Operator/Owner or
 * Payor/Payee) against each Source column (Autoprovision, Salesforce) side by
 * side, mirroring the non-typed PartiesTable. Built from the card's partySources
 * so it renders synchronously (no fetch). An empty source cell shows "Not
 * Available"; renders nothing when the doc carries no real source block.
 * @param {object} props The section.
 * @param {PartySourceBlock[] | null} [props.sources] The card's source blocks.
 * @param {{ open: boolean }} [props.openSignal] Expand/collapse-all broadcast.
 * @return {JSX.Element | null} The section.
 */
export function PartiesSourceSection({
  sources,
  openSignal,
}: {
  sources?: PartySourceBlock[] | null;
  // Broadcast from the card's Expand-All / Collapse-All control.
  openSignal?: { open: boolean };
}) {
  const blocks = (sources ?? []).filter((block) => block.source);
  if (blocks.length === 0) return null;

  const labels: string[] = [];
  blocks.forEach((block) =>
    block.rows.forEach((row) => {
      if (!labels.includes(row.label)) labels.push(row.label);
    })
  );
  const rows: Row[] = labels.map((label) => ({
    label,
    cells: blocks.map(
      (block) => block.rows.find((row) => row.label === label)?.parties ?? []
    ),
  }));

  return (
    <ExpandAccordion title="Parties" openSignal={openSignal}>
      <ExpandTable<Row>
        bordered
        columns={[
          { header: 'Parties', cell: (row: Row) => row.label },
          ...blocks.map((block, i) => ({
            header: block.sourceLabel,
            cell: (row: Row) => (
              <PartyCellList
                parties={row.cells[i]}
                emptyLabel={NOT_AVAILABLE}
              />
            ),
          })),
        ]}
        rows={rows}
        rowKey={(row) => row.label}
      />
    </ExpandAccordion>
  );
}
